import { useState, useEffect, useRef, useCallback } from 'react';

export const useInfiniteScroll = (fetchFunction, { groupId, selectedServiceId }) => {
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const observer = useRef();
    
    // 로딩과 hasMore 상태를 ref에 저장하여 콜백에서 최신 값을 사용
    const stateRef = useRef({ loading, hasMore });
    useEffect(() => {
        stateRef.current = { loading, hasMore };
    }, [loading, hasMore]);

    const loadItems = useCallback(async (currentPage, searchTerm = '') => {
        // 중복 로딩 방지
        if (stateRef.current.loading) return;
        
        setLoading(true);

        try {
            const pageResponse = await fetchFunction({ 
                groupId, 
                selectedServiceId, 
                page: currentPage, 
                size: 20,
                searchKeyword: searchTerm 
            });

            if (pageResponse && Array.isArray(pageResponse.content)) {
                setHasMore(!pageResponse.last);
                setItems(prevItems => {
                    const newItems = pageResponse.content.filter(newItem => 
                        !prevItems.some(prevItem => prevItem.profileId === newItem.profileId)
                    );
                    return currentPage === 0 ? newItems : [...prevItems, ...newItems];
                });
            } else {
                setHasMore(false); // 응답 형식이 잘못된 경우
            }
        } catch (error) {
            console.error("데이터 로딩 실패:", error);
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    }, [groupId, selectedServiceId, fetchFunction]);

    // IntersectionObserver 콜백 (의존성 배열 비움)
    const lastItemRef = useCallback(node => {
        if (observer.current) observer.current.disconnect();
        
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && stateRef.current.hasMore && !stateRef.current.loading) {
                setPage(prevPage => prevPage + 1);
            }
        });

        if (node) observer.current.observe(node);
    }, []); // ✨ 의존성 배열을 비워 observer가 불필요하게 재생성되지 않도록 함

    // 검색 또는 초기 로드를 위한 함수
    const search = useCallback((searchTerm = '') => {
        setItems([]);
        setPage(0);
        setHasMore(true);
    }, []);

    // 페이지 변경 또는 검색 시 데이터 로딩을 처리하는 useEffect
    useEffect(() => {
        // 검색으로 인해 items가 비워진 후 첫 페이지 로드
        if (page === 0 && items.length === 0 && hasMore && !loading) {
            loadItems(0, ''); 
        } 
        // 스크롤로 인한 다음 페이지 로드
        else if (page > 0 && hasMore && !loading) {
            loadItems(page, '');
        }
    }, [page, items.length, hasMore, loading, loadItems]); // ✨ 의존성 배열 명확화


    return { items, setItems, loading, hasMore, lastItemRef, search };
};